
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Dialog, Paper, InputBase, List, ListItemButton, ListItemIcon, ListItemText,
    Box, Typography, Divider, alpha, useTheme
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

const CommandKMenu = ({ open, onClose, apps }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(0);
    const inputRef = useRef(null);

    const filteredApps = useMemo(() => {
        if (!searchQuery) {
            return apps;
        }
        const lowerCaseQuery = searchQuery.toLowerCase();
        return apps.filter(app =>
            app.title.toLowerCase().includes(lowerCaseQuery) ||
            app.description.toLowerCase().includes(lowerCaseQuery)
        );
    }, [searchQuery, apps]);

    useEffect(() => {
        if (open) {
            // Resetta lo stato quando il menu viene aperto
            setSearchQuery('');
            setSelectedIndex(0);
        }
    }, [open]);

    const handleNavigation = (app) => {
        onClose();
        const isExternal = app.path.startsWith('http');
        if (isExternal) {
            window.open(app.path, '_blank');
        } else {
            navigate(app.path);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex(prev => (prev + 1) % filteredApps.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex(prev => (prev - 1 + filteredApps.length) % filteredApps.length);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (filteredApps[selectedIndex]) {
                handleNavigation(filteredApps[selectedIndex]);
            }
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: '100%',
                    maxWidth: '600px',
                    borderRadius: '12px',
                    boxShadow: theme.shadows[10],
                    backgroundColor: 'transparent' // Il colore sarà gestito dal Paper interno
                }
            }}
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: alpha(theme.palette.common.black, 0.6),
                        backdropFilter: 'blur(4px)',
                    }
                }
            }}
            // Allinea il dialogo in alto
            sx={{
                '& .MuiDialog-container': {
                    alignItems: 'flex-start',
                    paddingTop: '10vh'
                }
            }}
        >
            <Paper
                onKeyDown={handleKeyDown}
                sx={{
                    width: '100%',
                    backgroundColor: theme.palette.background.paper,
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: '12px',
                }}
            >
                <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center' }}>
                    <SearchIcon sx={{ color: 'text.secondary', mx: 1 }} />
                    <InputBase
                        autoFocus
                        inputRef={inputRef}
                        fullWidth
                        placeholder="Cerca un'applicazione..."
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setSelectedIndex(0);
                        }}
                        sx={{ fontSize: '1.1rem' }}
                    />
                    <Typography variant="caption" sx={{ color: 'text.disabled', px: 1, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                        ESC
                    </Typography>
                </Box>
                <Divider />
                <List sx={{ p: 1, maxHeight: '60vh', overflowY: 'auto' }}>
                    {filteredApps.length > 0 ? (
                        filteredApps.map((app, index) => (
                            <ListItemButton
                                key={app.path}
                                selected={selectedIndex === index}
                                onClick={() => handleNavigation(app)}
                                sx={{ borderRadius: '8px' }}
                            >
                                <ListItemIcon sx={{ color: app.color, minWidth: 40 }}>
                                    {React.createElement(app.icon)}
                                </ListItemIcon>
                                <ListItemText
                                    primary={app.title}
                                    secondary={app.description}
                                />
                            </ListItemButton>
                        ))
                    ) : (
                        <Typography sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                            Nessuna applicazione trovata.
                        </Typography>
                    )}
                </List>
            </Paper>
        </Dialog>
    );
};

export default CommandKMenu;